<template>
  <b-overlay :show="loading">
    <pdf-preview
      v-if="inPreview"
      :inPreview="inPreview"
      :generatingPreview="generatingPreview"
      :saving="saving"
      :pdfUrl="pdfUrl"
      :backToForm="backToForm"
      :save="save"
    />
    <div v-else>
      <form autocomplete="off" @submit.prevent>
        <div class="row">
          <div class="col-12">
            <h2>FORMULARIO CONVENIO DE PRESTACIÓN DE SERVICIOS A HONORARIOS</h2>
          </div>
          <div class="col-12">
            <b-form-group>
              <label for="previousDocument">
                ¿Deseas basarte en otro documento?
              </label>
              <v-select
                v-model="previousDocument"
                :options="createdDocuments"
                name="previousDocument"
                label="label"
                data-cy="created-document-data"
                @input="getDocumentData($event)"
                :clearable="true"
              >
                <template slot="no-options">
                  no hay documentos registrados
                </template>
              </v-select>
            </b-form-group>
          </div>
          <div class="col-12 mb-4" v-if="!attachments || !attachments.length">
            <b-button
              variant="secondary"
              class="btn-icon mt-1 w-100"
              data-cy= "from-repository"
              v-has-permission="'ED060'"
              v-b-modal="'add-repository-file'"
              :disabled="attachments.length >= 1"
            >
              <feather-icon icon="ServerIcon"/>
              Seleccionar SCH desde el repositorio
            </b-button>
          </div>
          <div class="col-12" v-for="file in attachments" :key="file.id">
            <div class="row align-items-center">
              <vs-icon class="col-2" icon="description" size="25px" color="primary" />
              <b class="w-100 text-center col-8">{{ file.name }}</b>
              <vs-button
                class="col-2"
                color="danger"
                type="flat"
                icon="clear"
                @click="removeRepositoryFile(file.id)"
              />
            </div>
          </div>
          <div class="col-12" v-if="!attachments || !attachments.length">
            <span class="danger span-text-validation-danger v-enter-to">
              <small>
                Debes seleccionar un archivo desde el repositorio
              </small>
            </span>
          </div>
        </div>
        <div class="row" v-if="attachments && attachments.length && !loading">
          <div class="col-12">
            <vs-divider />
          </div>
          <div class="col-6">
            <b-form-group>
              <label for="agreementDate">
                Fecha convenio
                <span v-html="required('agreementDate')"/>
              </label>
              <date-picker
                :class="{
                  'w-100': true,
                  'danger': $validator.errors.has('agreementDate'),
                }"
                data-cy='agreementDate'
                name='agreementDate'
                data-vv-as='fecha convenio'
                v-model="formData.agreementDate"
                format='DD/MM/YYYY'
                value-type='DD/MM/YYYY'
                placeholder='Ej: 30/12/2000'
                v-validate="'required'"
                :danger="$validator.errors.has('agreementDate')"
              />
              <v-invalid-feedback v-if="$validator.errors.has('agreementDate')">
                {{ $validator.errors.first('agreementDate') }}
              </v-invalid-feedback>
            </b-form-group>
          </div>
          <div class="col-6 mt-3">
            <b-form-group class="">
              <b-form-checkbox
                name="regularizesAgreement"
                data-cy="regularizes-agreement"
                data-vv-as="¿Esta solicitud regulariza convenio?"
                v-model="formData.regularizesAgreement"
                class="m-0"
              >
                ¿Esta solicitud regulariza convenio?
              </b-form-checkbox>
            </b-form-group>
          </div>
          <div class="col-6" v-if="formData.nationality !== 'Chilena'">
            <b-form-group>
              <vs-checkbox
                v-model="formData.hasResidence"
                color="primary"
                class="mb-3"
              >
                Tiene residencia definitiva
              </vs-checkbox>
              <v-invalid-feedback v-if="$validator.errors.has('hasResidence')">
                {{ $validator.errors.first('hasResidence') }}
              </v-invalid-feedback>
            </b-form-group>
          </div>
          <div class="col-12">
            <b-form-group>
              <label for="functionDate">
                Asumirá funciones desde
                <span v-html="required('functionDate')"/>
              </label>
              <date-picker
                :class="{
                  'w-100': true,
                  'danger': $validator.errors.has('functionDate'),
                }"
                data-cy='functionDate'
                name='functionDate'
                data-vv-as='asumirá funciones desde'
                v-model="formData.functionDate"
                format='DD/MM/YYYY'
                value-type='DD/MM/YYYY'
                placeholder='Ej: 30/12/2000'
                v-validate="'required'"
                :danger="$validator.errors.has('functionDate')"
              />
              <v-invalid-feedback v-if="$validator.errors.has('functionDate')">
                {{ $validator.errors.first('functionDate') }}
              </v-invalid-feedback>
            </b-form-group>
          </div>
          <div class="col-12">
            <b-form-group>
              <label for="first">
                Primero:
                <span v-html="required('first')"/>
              </label>
              <editor-text
                data-cy='first'
                name='first'
                data-vv-as='primero'
                placeholder='PRIMERO: ...'
                :max-length="5000"
                ref="third"
                v-model="formData.first"
                v-validate="'required'"
                with-counter
              />
              <v-invalid-feedback v-if="$validator.errors.has('first')">
                {{ $validator.errors.first('first') }}
              </v-invalid-feedback>
            </b-form-group>
          </div>
          <div v-if="formData.functionDate">
            <div class="col-12" v-if="formData.isDocenteForm">
              <b-form-group>
                <label for="third">
                  Tercero:
                  <span v-html="required('third')"/>
                </label>
                <editor-text
                  data-cy='third'
                  name='third'
                  data-vv-as='tercero'
                  placeholder='TERCERO: ...'
                  :max-length="5000"
                  ref="third"
                  v-model="formData.third"
                  v-validate="'required'"
                  with-counter
                />
                <v-invalid-feedback v-if="$validator.errors.has('third')">
                  {{ $validator.errors.first('third') }}
                </v-invalid-feedback>
              </b-form-group>
            </div>
            <div class="col-12 mb-4" v-if="formData.isNotDocenteForm">
              <vs-table
                hoverFlat
                class="wrap-table border"
                :data="formData.assocProductsAndServices || []"
                noDataText="Aún no se han agregado productos y servicios asociados"
              >
                <template slot="header">
                  <span class="w-100 p-2 text-center font-bold">
                    Servicios y productos asociados
                  </span>
                </template>
                <template slot="thead">
                  <th>
                    Mes
                  </th>
                  <th>
                    Producto
                  </th>
                  <th>
                    Valoración
                  </th>
                  <th>
                    Verificador
                  </th>
                </template>
                <template slot-scope="{data}">
                  <tbody>
                  <vs-tr
                    :key="idx"
                    v-for="(_, idx) in data"
                  >
                    <td
                      class="p-1"
                      style="width: 20%"
                    >
                      <b-form-textarea
                        :rows="data[idx].month.toString().split('\n').length + 1"
                        :name="`assocProductsAndServices[${idx}].month`"
                        :data-cy='`assocProductsAndServices[${idx}].month`'
                        data-vv-as='mes'
                        placeholder='Mes...'
                        v-model="data[idx].month"
                        :state="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].month`)
                          ? false : null
                        "
                        :danger="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].month`)
                        "
                        :danger-text="$validator
                          .errors
                          .first(`assocProductsAndServices[${idx}].month`)
                        "
                        v-validate="'required'"
                      />
                      <v-invalid-feedback
                        v-if="$validator.errors.has(`assocProductsAndServices[${idx}].month`)"
                      >
                        {{
                          $validator.errors.first(`assocProductsAndServices[${idx}].month`)
                        }}
                      </v-invalid-feedback>
                    </td>
                    <td
                      class="p-1"
                      style="width: 30%"
                    >
                      <b-form-textarea
                        :rows="data[idx].product.toString().split('\n').length + 1"
                        :name="`assocProductsAndServices[${idx}].product`"
                        :data-cy='`assocProductsAndServices[${idx}].product`'
                        data-vv-as='producto'
                        placeholder='Producto...'
                        v-model="data[idx].product"
                        :state="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].product`)
                          ? false : null
                        "
                        :danger="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].product`)
                        "
                        :danger-text="$validator
                          .errors
                          .first(`assocProductsAndServices[${idx}].product`)
                        "
                        v-validate="'required'"
                      />
                      <b-form-invalid-feedback
                        v-if="$validator.errors.has(`assocProductsAndServices[${idx}].product`)"
                      >
                        {{
                          $validator.errors.first(`assocProductsAndServices[${idx}].product`)
                        }}
                      </b-form-invalid-feedback>
                    </td>
                    <td
                      class="p-1"
                      style="width: 20%"
                    >
                      <b-form-textarea
                        :rows="data[idx].valuation.toString().split('\n').length + 1"
                        :name="`assocProductsAndServices[${idx}].valuation`"
                        :data-cy='`assocProductsAndServices[${idx}].valuation`'
                        data-vv-as='Valoración'
                        placeholder='Valorización...'
                        v-model="data[idx].valuation"
                        :state="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].valuation`)
                          ? false : null
                        "
                        :danger="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].valuation`)
                        "
                        :danger-text="$validator
                          .errors
                          .first(`assocProductsAndServices[${idx}].valuation`)
                        "
                        v-validate="'required'"
                      />
                      <b-form-invalid-feedback
                        v-if="$validator
                          .errors
                          .has(
                            `assocProductsAndServices[${idx}].valuation`
                          )
                        "
                      >
                        {{
                          $validator
                            .errors
                            .first(`assocProductsAndServices[${idx}].valuation`)
                        }}
                      </b-form-invalid-feedback>
                    </td>
                    <td
                      class="p-1"
                      style="width: 30%"
                    >
                      <b-form-textarea
                        :rows="data[idx].verificator.toString().split('\n').length + 1"
                        :name="`assocProductsAndServices[${idx}].verificator`"
                        :data-cy='`assocProductsAndServices[${idx}].verificator`'
                        data-vv-as='verificador'
                        placeholder='Verificador...'
                        v-model="data[idx].verificator"
                        :state="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].verificator`)
                          ? false : null
                        "
                        :danger="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].verificator`)
                        "
                        :danger-text="$validator
                          .errors
                          .first(`assocProductsAndServices[${idx}].verificator`)
                        "
                        v-validate="'required'"
                      />
                      <b-form-invalid-feedback
                        v-if="$validator
                          .errors
                          .has(`assocProductsAndServices[${idx}].verificator`)
                        "
                      >
                        {{
                          $validator
                            .errors
                            .first(`assocProductsAndServices[${idx}].verificator`)
                        }}
                      </b-form-invalid-feedback>
                    </td>
                  </vs-tr>
                  </tbody>
                </template>
              </vs-table>
            </div>
            <div class="col-12">
              <b-form-group>
                <label for="installmentsBreakdown">
                  Desglose del periodo de pago
                  <span v-html="required('installmentsBreakdown')"/>
                </label>
                <editor-text
                  data-cy='installmentsBreakdown'
                  name='installmentsBreakdown'
                  data-vv-as='desglose de cuotas'
                  placeholder='Desglose de cuotas...'
                  :max-length="5000"
                  ref="installmentsBreakdown"
                  v-model="formData.installmentsBreakdown"
                  v-validate="'required'"
                  with-counter
                />
                <v-invalid-feedback v-if="$validator.errors.has('installmentsBreakdown')">
                  {{ $validator.errors.first('installmentsBreakdown') }}
                </v-invalid-feedback>
              </b-form-group>
            </div>
            <div class="col-12">
              <b-form-group>
                <label for="ninth">
                  Noveno:
                  <span v-html="required('ninth')"/>
                </label>
                <editor-text
                  data-cy='ninth'
                  name='ninth'
                  data-vv-as='noveno'
                  placeholder='NOVENO: ...'
                  :max-length="5000"
                  ref="ninth"
                  v-model="formData.ninth"
                  v-validate="'required'"
                  with-counter
                />
                <v-invalid-feedback v-if="$validator.errors.has('ninth')">
                  {{ $validator.errors.first('ninth') }}
                </v-invalid-feedback>
              </b-form-group>
            </div>
            <div class="col-12">
              <b-form-group>
                <label for="agreementProvides">
                  El convenio contempla:
                  <span v-html="required('agreementProvides')"/>
                </label>
                <editor-text
                  data-cy='agreementProvides'
                  name='agreementProvides'
                  data-vv-as='el convenio contempla'
                  placeholder='El convenio contempla...'
                  :danger="$validator.errors.has('agreementProvides')"
                  :danger-text="$validator.errors.first('agreementProvides')"
                  :state="$validator.errors.has('agreementProvides') ? false : null"
                  :max-length="5000"
                  ref="agreementProvides"
                  v-model="formData.agreementProvides"
                  with-counter
                />
                <v-invalid-feedback v-if="$validator.errors.has('agreementProvides')">
                  {{ $validator.errors.first('agreementProvides') }}
                </v-invalid-feedback>
              </b-form-group>
            </div>
          </div>
          <div class="col-12">
            <b-form-group>
              <label for="first">
                Quinto:
                <span v-html="required('fifth')"/>
              </label>
              <editor-text
                data-cy='fifth'
                name='fifth'
                data-vv-as='quinto'
                placeholder='QUINTO: ...'
                :max-length="5000"
                ref="third"
                v-model="formData.fifth"
                v-validate="'required'"
                with-counter
              />
              <v-invalid-feedback v-if="$validator.errors.has('fifth')">
                {{ $validator.errors.first('fifth') }}
              </v-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </form>
      <small>
        <b>Los campos marcados con <span class="text-danger">*</span> son obligatorios</b>
      </small>
      <form-action-buttons
        :saving="saving"
        :preview="preview"
        :back="back"
        :save="save"
        :saveDraft="saveDraft"
      />
    </div>
    <repository-selector
      :add-file="addRepositoryFile"
      :files="attachments"
      :remove-file="removeRepositoryFile"
      :max-files="1"
      required-file-type="SCH"
      :required-approval-state="approvalStateTypes.APPROVED"
    />
  </b-overlay>
</template>
<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/es';

import { ioCreateCustomDocument, ioGetCustomDocument } from '@/febos/servicios/api/customDocuments.api';
import { showToast } from '@/febos/global/customDocuments/vistas/empresas/61980520-8/utils';
import PdfPreview from '@/febos/global/customDocuments/vistas/empresas/61980520-8/components/PdfPreview';
import FormActionButtons from '@/febos/global/customDocuments/vistas/empresas/61980520-8/components/FormActionButtons';
import RepositorySelector from '@/febos/global/customDocuments/components/repository-selector/RepositorySelector';
import VInvalidFeedback from '@/febos/global/customDocuments/components/VInvalidFeedback';
import EditorText from '@/febos/global/customDocuments/vistas/empresas/61980520-8/components/EditorText';
import ApprovalStateTypes from '@/febos/global/documentManagement/mixins/ApprovalStateTypes';
import { numToWords } from '@/febos/global/customDocuments/utils/numToWords';
import { addDotsToNumber } from '@/febos/global/customDocuments/utils/addDotsToNumber';
import HandleDraft from "@/febos/global/documentManagement/mixins/HandleDraft";
import HandlePreLoadData from "@/febos/global/documentManagement/mixins/HandlePreLoadData";
import { mapActions, mapGetters } from 'vuex';
// import dataCSH from "@/febos/global/customDocuments/vistas/empresas/61980520-8/dataCpsh";

export default {
  name: 'CPSH',
  components: {
    EditorText,
    DatePicker,
    VInvalidFeedback,
    RepositorySelector,
    FormActionButtons,
    PdfPreview,
  },
  mixins: [HandleDraft, HandlePreLoadData],
  data() {
    return {
      formData: {
        assocProductsAndServices: [],
        nationality: '',
        hasResidence: false,
        regularizesAgreement: false,
        agreementDate: this.dateToStrDate(new Date()),
        agreementDateText: this.dateToString(new Date()),
        agreementYear: new Date().getFullYear(),

        functionDate: null,
        functionDateText: '',
        first: '',
        fifth: '',
        third: '',

        ninth: '<p><strong>NOVENO:</strong> Se deja expresamente '
          + 'establecido que el gasto que demande el cumplimiento del '
          + 'presente convenio debe imputarse al...</p>',
        installmentsBreakdown: '<p>Desglose de cuotas...</p>',

        agreementProvides: '',
        retentionPercentage: '15,25', // Valor por defecto
      },
      saving: false,
      error: {
        message: ''
      },

      // PDF Preview
      inPreview: false,
      generatingPreview: false,
      pdfUrl: '',

      // Repository selector
      attachments: [],

      // Selected File Loading
      loading: false,

      approvalStateTypes: ApprovalStateTypes,
    };
  },
  computed: {
    ...mapGetters('Herramientas', ['categoryRetentionPercentage']),
  },
  methods: {
    ...mapActions('Herramientas', ['listCategories']),
    async loadRetentionPercentage() {
      try {
        await this.listCategories({
          grupoOpcion: 'porcentaje-retencion-cpsh',
          deshabilitado: 'si'
        });

        if (this.categoryRetentionPercentage && this.categoryRetentionPercentage.length > 0) {
          this.formData.retentionPercentage = this.categoryRetentionPercentage[0].valor || '15,25';
        }
      } catch (error) {
        // Usar valor por defecto si falla
        console.warn('Error al cargar porcentaje de retención, usando valor por defecto:', error);
        this.formData.retentionPercentage = '15,25';
      }
    },
    required(fieldName) {
      const item = this.$validator.fields.items.find((_item) => _item.name === fieldName);
      if (!item || !item.rules || !Object.keys(item.rules).length || !Object.keys(item.rules).includes('required')) {
        return '';
      }
      return "<span class='text-danger'><b>*</b></span>";
    },
    dateToStrDate(date) {
      if (!date) return null;
      const formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
      const formatter = new Intl.DateTimeFormat('es-ES', formatOptions);

      return formatter.format(date);
    },
    dateStrToDate(dateStr) {
      if (!dateStr) return null;
      const [day, month, year] = dateStr.split(dateStr.includes('/') ? '/' : '-').map(Number);
      return new Date(year, month - 1, day);
    },
    dateToString(date) {
      const formatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
      const formatter = new Intl.DateTimeFormat('es-ES', formatOptions);

      return formatter.format(date);
    },
    // repo selector:
    async getSelectedFileData(file) {
      try {
        this.loading = true;
        const { id } = file;
        const { data } = await ioGetCustomDocument('SCH', id, {});
        // const data = dataCSH;

        data.document = {
          ...data.document,
          schCompany: data.document.company,
          schCompanyName: data.document.companyName,
          schCreatedAt: data.document.createdAt,
          schCreatedBy: data.document.createdBy,
          schCreatedName: data.document.createdName,
          schDocumentType: data.document.documentType,
          schId: data.document.id,
          schLocationId: data.document.locationId,
          schLocationType: data.document.locationType,
          schTrackingId: data.document.trackingId,
        };

        delete data.document.company;
        delete data.document.companyName;
        delete data.document.createdAt;
        delete data.document.createdBy;
        delete data.document.createdName;
        delete data.document.documentType;
        delete data.document.id;
        delete data.document.locationId;
        delete data.document.locationType;
        delete data.document.trackingId;

        const paymentPeriodBreakdown = data.document.paymentPeriodBreakdown || [];
        const installmentsBreakdownLines = paymentPeriodBreakdown
          .map((item) => (
            `${item.description || ''} por un monto bruto de $${addDotsToNumber(item.amount || 0)}
            (${this.amountToWords(item.amount || 0)}) con cargo al proyecto ${item.project || ''}`
          ));

        const paymentPeriodLength = paymentPeriodBreakdown.length;

        const hasProvidements = !!data.document.travelAllowance
          || !!data.document.landPassageRefund
          || !!data.document.training;

        const workHoursAndRemuneration = data.document.workHoursAndRemuneration || [];
        const docentBreakdown = workHoursAndRemuneration
          .map((item) => `<li>${item.docentHours || 0} hora${parseFloat(item.docentHours || 0) > 1 ? 's' : ''} docente
            - ${item.otherActivitiesHours || 0} hora${parseFloat(item.otherActivitiesHours || 0) > 1 ? 's' : ''} otras actividades
            - ${item.subjectName || ''}
            - ${item.subjectCode || ''}</li>`).join('');

        const totalDocentHours = workHoursAndRemuneration
          .reduce((acc, item) => acc + parseFloat(item.docentHours || 0), 0);

        const totalOtherActivitiesHours = workHoursAndRemuneration
          .reduce((acc, item) => acc + parseFloat(item.otherActivitiesHours || 0), 0);

        // console.log('DAT', data);
        // console.log('docentBreakdown', docentBreakdown);

        let funciones;
        if (data.document.specificLaborArray && data.document.specificLaborArray.length) {
          funciones = data.document.specificLaborArray.map((item) => `<p>${item.task || ''}</p>`).join('');
        } else {
          funciones = data.document.specificLabor || '';
        }
        const administrativeResponsible = data.document.administrativeResponsibleComplete
          ? (data.document.administrativeResponsibleComplete.label || '') : (data.document.administrativeResponsible || '');
        const otherResponsibles = data.document.otherResponsibleComplete
          ? (data.document.otherResponsibleComplete.label || '') : (data.document.otherResponsibles || '');

        // Leer isPublicAgent con fallback a false para compatibilidad hacia atrás
        const isPublicAgent = data.document.isPublicAgent || false;

        const sixthClause = isPublicAgent
          ? 'La persona contratada, a través de la declaración jurada simple adjunta, la cual se entiende formar parte integrante del presente convenio, señala no estar afecta a las inhabilidades de los artículos 54 y 56 del DFL N°1/19.653, del año 2000, del Ministerio Secretaría General de la Presidencia, que fija el texto refundido, coordinado y sistematizado de la Ley N°18.575, Orgánica de Bases Generales de la Administración del Estado. Asimismo, de conformidad con lo dispuesto en la normativa vigente, la persona contratada adquiere la calidad de Agente Público en el desempeño de las funciones encomendadas.'
          : 'La persona contratada, a través de la declaración jurada simple adjunta, la cual se entiende formar parte integrante del presente convenio, señala no estar afecta a las inhabilidades de los artículos 54 y 56 del DFL N°1/19.653, del año 2000, del Ministerio Secretaría General de la Presidencia, que fija el texto refundido, coordinado y sistematizado de la Ley N°18.575, Orgánica de Bases Generales de la Administración del Estado.';

        this.formData = {
          ...this.formData,
          ...data.document,
          docentBreakdown,
          nationality: data.document.nationality || '',
          hasResidence: data.document.nationality === 'Chilena',
          totalDocentHours,
          totalOtherActivitiesHours,
          isPublicAgent,
          sixth: sixthClause,
          first: `<p> <b>PRIMERO:</b> Don/Doña <b><u> ${data.document.fullName || ''}</u></b>, en su calidad de ${data.document.title || ''},
            se compromete a efectuar el siguiente cometido específico: </p> ${funciones} <p>Dicha labor
            será supervisada por el/la ${data.document.administrativeResponsiblePosition || ''}, ${administrativeResponsible},
            o en su defecto quien le subrogue. </p> <p>${otherResponsibles} </p>`,
          installmentsBreakdown: `La Universidad de Aysén pagará a título de honorario a la prestadora de servicios la suma bruta
            total de $${addDotsToNumber(data.document.totalAmount || 0)}.- (${this.amountToWords(data.document.totalAmount || 0)})
            pagadera en ${paymentPeriodLength} (${this.amountToWords(paymentPeriodLength, false)}) ${paymentPeriodLength > 1 ? 'cuotas' : 'cuota'},
            desglosados de la siguiente manera: ${installmentsBreakdownLines.join('; ')}.
            ${paymentPeriodLength > 1 ? ' Todas' : ' Pagadera'} contra entrega de productos.
            Dichos pagos se harán efectivos dentro de los primeros cinco días hábiles del mes siguiente al de
            la prestación efectiva de la labor. Al honorario que perciba la persona contratada se le descontará
            la retención legal de ${this.formData.retentionPercentage || '15,25'}% o, en su defecto, la retención del año en curso según corresponda a lo
            informado por el Servicio de Impuestos Internos, y se exigirá la entrega de boleta electrónica de
            honorarios, Informe de Actividades que contenga una descripción del trabajo realizado y el
            respectivo Visto Bueno por parte del Jefe de Departamento ${(data.document.groupBelongsUser?.nombre || '').trim()},
            académico/a ${administrativeResponsible}, en su calidad de supervisor de la labor convenida.`,
          fifth: `<p> <b>QUINTO:</b> El/La Supervisor/a, con cada cuota a pagar,
            deberá remitir a la Dirección de Administración y Finanzas, el "certificado de cumplimiento de
            labores, informe de actividades y autorización de pago de boletas de honorarios", debiendo incluir
            el número total de horas realizadas, a efectos de verificar el cumplimiento cabal de las
            disposiciones estipuladas en este convenio.</p>`,
          agreementProvides: hasProvidements
            ? `<ul>
                ${data.document.travelAllowance ? `<li>Derecho a viáticos: ${data.document.travelAllowanceJustification || ''}</li>` : ''}
                ${data.document.landPassageRefund ? `<li>Reembolsos pasajes terrestres y/o combustible: ${data.document.landPassageRefundJustification || ''}</li>` : ''}
                ${data.document.training ? `<li>Capacitaciones: ${data.document.trainingJustification || ''}</li>` : ''}
              </ul>`
            : '',
          assocProductsAndServices: [...(data.document.associatedProduct || []).map((item) => ({
            month: item.period || '',
            product: item.product || '',
            valuation: `$${addDotsToNumber(item.bruteValuation || 0)}`,
            verificator: item.subproduct || '',
          }))],
        };
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.$validator.validateAll();
        });
      }
    },
    setThirdData() {
      this.$set(this.formData, 'third', `<p><b>TERCERO:</b> El servicio convenido tendrá vigencia a contar del ${this.formData.contractValidityDateStartText || ''} al ${this.formData.contractValidityDateEndText || ''}, ambas fechas inclusive, y/o mientras sus servicios sean necesarios. Por razones de buen servicio, la persona contratada asumirá funciones desde el ${this.formData.functionDateText}, aun cuando no esté totalmente tramitado el acto administrativo que lo apruebe.</p>
        <p>El/la prestador/a de servicios realizará durante la vigencia del presente convenio a honorarios, ${this.formData.totalDocentHours || 0} horas de docencia y ${this.formData.totalOtherActivitiesHours || 0} horas para otras actividades de apoyo a la docencia, en las condiciones dispuestas por el Departamento de ${this.formData.groupBelongsUser?.nombre || ''}.

        <p>Sin perjuicio de la vigencia del presente convenio, la Universidad podrá poner término anticipado en forma pura y simple, sin necesidad de aviso previo, debiendo notificar por escrito a la parte afectada de dicha medida, ya sea personalmente o por carta certificada. En ambos casos se debe indicar claramente la fecha en que se entiende finiquitado el servicio.</p>`);
    },
    amountToWords(amount, withPesos = true, lowercase = true) {
      const suffix = parseFloat(amount) === 1 ? ' peso' : ' pesos';
      const amountText = `${numToWords(parseFloat(amount))}${withPesos ? suffix : ''}`;
      return lowercase ? amountText.toLowerCase() : amountText;
    },
    async addRepositoryFile(file) {
      if (!Object.keys(file).length) {
        return;
      }

      if (file.failed) {
        this.error.message = 'La carga del archivo falló, inténtalo de nuevo';
      }

      this.attachments.push(file);
      this.error.message = '';

      await this.getSelectedFileData(file);
    },
    removeRepositoryFile(id) {
      this.attachments = this.attachments.filter((file) => file.id !== id);
    },
    // others
    back() {
      window.history.back();
    },
    async validateForm() {
      const validForm = await this.$validator.validateAll();
      const validAttachments = this.attachments && this.attachments.length;

      const result = !!validForm && validAttachments;

      if (!result) {
        this.$vs.notify({
          title: 'Ups!',
          text: 'Revisa los campos marcados en rojo',
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'bottom-right',
          time: 5000,
        });
      }
      return result;
    },
    getAgreementProvides() {
      if (!this.formData.agreementProvides || this.formData.agreementProvides === '') {
        return {};
      }
      if (this.formData.agreementProvides.includes('DÉCIMO TERCERO')) {
        return { agreementProvides: this.formData.agreementProvides };
      }
      return { agreementProvides: `<p><b>DÉCIMO TERCERO:</b></p>${this.formData.agreementProvides}` };
    },
    getInstallmentsBreakdown() {
      return this.formData.installmentsBreakdown.startsWith('<p>')
      && this.formData.installmentsBreakdown.endsWith('</p>')
        ? this.formData.installmentsBreakdown.split('').slice(3, -4).join('')
        : this.formData.installmentsBreakdown;
    },
    getFormData() {
      return {
        ...this.formData,
        ...(this.getAgreementProvides()),
        attachments: this.attachments || [],
        installmentsBreakdown: this.getInstallmentsBreakdown(),
      };
    },
    async save() {
      this.$validator.errors.clear();
      const isValid = await this.validateForm();
      if (!isValid) {
        return;
      }

      this.error.message = '';
      this.saving = true;
      const from = this.$route.query?.from;
      try {
        showToast(this, 'Guardando documento', 'En camino...', 'primary', 2000);

        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            numerar: 'si',
            ...(from === 'draft' && { replace: 'si' })
          },
          formData: {
            ...this.getFormData(),
            status: 1,
          },
        };

        const response = await ioCreateCustomDocument(payload);
        await this.notifyCreation(response.data.name || '');
        await this.generatePdf(response.data.document.id);
      } finally {
        this.saving = false;
      }
    },
    async saveDraft() {
      this.$validator.errors.clear();

      this.error.message = '';
      this.saving = true;
      const { from } = this.$route.query;
      try {
        showToast(this, 'Guardando borrador', 'En camino...', 'primary', 2000);

        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            numerar: 'no',
            ...(from === 'draft' && { replace: 'si' })
          },
          formData: {
            ...this.getFormData(),
            status: '3',
          },
        };

        const response = await ioCreateCustomDocument(payload);
        const docId = response.data?.document?.id || response.data?.documentId;
        if (docId) {
          await this.updateRouteAsDraft(docId);
        }
        showToast(this, 'Borrador guardado correctamente', '¡Listo!', 'success', 3000);
      } finally {
        this.saving = false;
      }
    },
    backToForm() {
      this.inPreview = false;
    },
    async preview() {
      const isValid = await this.validateForm();
      if (!isValid || !this.attachments || !this.attachments.length) {
        return;
      }

      this.pdfUrl = '';
      this.inPreview = true;
      this.generatingPreview = true;

      try {
        showToast(this, 'Generando previsualización', 'En camino...', 'primary', 3000);

        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            simular: 'si',
          },
          formData: {
            ...this.getFormData(),
            borrador: true,
          }
        };

        const { data: { pdf } } = await ioCreateCustomDocument(payload);

        if (!pdf || pdf === '') {
          showToast(this, 'No se pudo generar la previsualización', 'Ups!', 'warning', 5000);
          this.backToForm();
        }

        this.pdfUrl = `${pdf}#toolbar=0&navpanes=0&scrollbar=0`;
      } finally {
        this.generatingPreview = false;
      }
    },
    async generatePdf(id) {
      showToast(this, 'Generando archivo PDF', 'En camino...', 'primary', 5000);
      const response = await ioGetCustomDocument('CPSH', id, { pdf: 'si' });
      showToast(this, 'Archivo generado', 'Listo!', 'primary', 2000);
      window.open(response.data.pdf, '_blank');
      /* eslint-disable no-restricted-globals */
      history.back();
    },
  },
  watch: {
    'formData.agreementDate': {
      handler(val) {
        this.formData.agreementDateText = this.dateToString(
          this.dateStrToDate(val)
        );

        this.formData.agreementYear = this.dateStrToDate(val).getFullYear();
      },
    },
    'formData.functionDate': {
      handler(val) {
        this.formData.functionDateText = this.dateToString(
          this.dateStrToDate(val)
        );
        this.setThirdData();
      },
    },
  },
  async created() {
    this.loadRetentionPercentage();
  }
};
</script>
<style>
.ck-editor ul {
  padding-left: 18px;
}
</style>
